// Net-casting from the boat: hauls in a whole school at once.
// Extracted from legacy/tsuri.html — logic and constants are unchanged.
//
// Another subsystem with no assigned step (see world/weather.ts's header
// for why it gets its own file instead of living in main.ts).

import * as THREE from 'three';
import { WATER_SURF_Y } from '@world/water';
import { PROP_SCALE } from '@world/terrain';
import { toonMat } from '@world/toon';
import { boat } from '@systems/building';
import { cast, endCast, computeFishingBlockReason, type FishingConditions } from '@systems/fishing';
import { collectFishWithin, FISH_TYPES } from '@entities/fish';
import { BOAT_CAP, boatFish, makeFish, recordCatch } from '@economy/market';

type BlockConditions = Pick<FishingConditions, 'isStormy' | 'inBoat' | 'isNight' | 'isFullMoon'>;

export interface Net {
  active: boolean;
  timer: number;
  x: number;
  z: number;
  mesh: THREE.Mesh | null;
}
export const net: Net = { active: false, timer: 0, x: 0, z: 0, mesh: null };
let netCooldown = 0;

export const netGroup = new THREE.Group();

export function netCooldownRemaining(): number {
  return netCooldown;
}

function ensureNetMesh(): THREE.Mesh {
  if (!net.mesh) {
    // The ring's radius marks the actual catch area, so it stays in world
    // units; only the cord's thickness follows PROP_SCALE.
    const torus = new THREE.TorusGeometry(1.15, 0.028 * PROP_SCALE, 6, 22);
    torus.rotateX(Math.PI / 2);
    net.mesh = new THREE.Mesh(torus, toonMat(0xc7b489));
    netGroup.add(net.mesh);
  }
  return net.mesh;
}

export function castNet(cond: BlockConditions): { success: boolean; reason?: string } {
  const block = computeFishingBlockReason(cond);
  if (block) return { success: false, reason: block };
  if (boatFish.length >= BOAT_CAP) return { success: false, reason: 'boat-full' };
  if (!boat) return { success: false, reason: 'no-boat' };
  if (cast.active) endCast();
  net.active = true;
  net.timer = 5;
  net.x = boat.x;
  net.z = boat.z;
  const mesh = ensureNetMesh();
  mesh.visible = true;
  mesh.scale.setScalar(1);
  mesh.position.set(net.x, WATER_SURF_Y + 0.01, net.z);
  return { success: true };
}

// Used by the sleep mechanic (economy/rest.ts) to cut a cast short.
export function cancelNetIfActive(): void {
  if (net.active && net.mesh) {
    net.active = false;
    net.mesh.visible = false;
  }
}

export type NetEvent = { type: 'blocked'; reason: string } | { type: 'hauled'; count: number } | { type: 'empty' };

export function updateNet(dt: number, now: number, cond: BlockConditions): NetEvent | null {
  if (netCooldown > 0) netCooldown -= dt;
  if (!net.active) return null;
  const block = computeFishingBlockReason(cond);
  if (block) {
    net.active = false;
    if (net.mesh) net.mesh.visible = false;
    return { type: 'blocked', reason: block };
  }
  net.timer -= dt;
  if (net.mesh) {
    net.mesh.scale.setScalar(0.85 + 0.15 * (net.timer / 5)); // slowly cinching
    net.mesh.position.y = WATER_SURF_Y + 0.01 + Math.sin(now * 0.004) * 0.012;
  }
  if (net.timer <= 0) {
    const limit = BOAT_CAP - boatFish.length;
    const caught = limit > 0 ? collectFishWithin(net.x, net.z, 1.2, limit) : [];
    caught.forEach((f) => {
      const nm = FISH_TYPES[f.typeIndex].name;
      boatFish.push(makeFish(nm));
      recordCatch(nm, net.x, net.z);
    });
    net.active = false;
    if (net.mesh) net.mesh.visible = false;
    netCooldown = 20;
    return caught.length ? { type: 'hauled', count: caught.length } : { type: 'empty' };
  }
  return null;
}
